/**
 * API keys, in settings.
 *
 * The important moment is creation: the secret exists for exactly as long as
 * this panel shows it, because the server keeps only a hash. So the new key is
 * put on screen in the shape somebody actually needs it - the two environment
 * lines a bot expects - with a copy button, and a warning that it will not be
 * shown again. Anything less and people close the dialog and lose it.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { copyText } from '@/lib/clipboard';
import { openExternal } from '@/lib/bridge';
import { Button, Card, CardHead, Input, Tag, useConfirm, useToast } from './ui';

type Key = {
  id: string; name: string; scopes: string[];
  createdAt: number; lastUsedAt: number | null; calls: number; hint: string;
};

type Made = { clientId: string; clientSecret: string };

export function ApiKeysCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState<Made | null>(null);

  const q = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<{ keys: Key[]; scopes: string[]; max: number; ratePerMinute: number }>('/api/keys')
  });

  const keys = q.data?.keys ?? [];
  const full = keys.length >= (q.data?.max ?? 5);

  async function create() {
    setBusy(true);
    try {
      const res = await api.post<Made>('/api/keys', { name: name.trim() || 'Untitled key' });
      setMade({ clientId: res.clientId, clientSecret: res.clientSecret });
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const env = made
    ? 'PUGO_API_BASE=https://pugo.ink\n'
      + 'PUGO_BOT_CLIENT_ID=' + made.clientId + '\n'
      + 'PUGO_BOT_CLIENT_SECRET=' + made.clientSecret
    : '';

  return (
    <Card>
      <CardHead
        title="API keys"
        note={q.data ? keys.length + ' / ' + q.data.max : undefined}
        actions={
          <button
            type="button"
            onClick={() => openExternal('https://pugo.ink/developers')}
            className="text-[12.5px] font-semibold text-accent-hi hover:underline"
          >
            Read the docs
          </button>
        }
      />

      <p className="mb-3 text-[12.5px] text-mute">
        For a bot or a website that wants to read Pugo — leaderboards, players and crews.
        Keys can only read; there is nothing they can change.
      </p>

      {/* The one time the secret exists. */}
      {made ? (
        <div className="mb-3 rounded-[var(--radius)] border border-accent-line bg-accent-soft p-3">
          <div className="mb-1.5 text-[11px] font-extrabold tracking-[.1em] text-accent-hi uppercase">
            Copy this now
          </div>
          <p className="mb-2 text-[12px] text-dim">
            The secret is stored hashed, so this is the only time it can be shown.
          </p>
          <pre className="selectable mb-2 overflow-x-auto rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-inset p-2.5 font-mono text-[11.5px] leading-relaxed">
            {env}
          </pre>
          <div className="flex gap-2">
            <Button size="sm" kind="primary"
              onClick={() => { void copyText(env); toast('Copied', 'good'); }}>
              Copy
            </Button>
            <Button size="sm" kind="ghost" onClick={() => setMade(null)}>Done</Button>
          </div>
        </div>
      ) : null}

      {keys.length ? (
        <div className="mb-3 flex flex-col gap-1">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-2.5 rounded-[var(--radius)] border border-line-soft bg-surface-2 px-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-bold">{k.name}</span>
                <span className="block truncate font-mono text-[11px] text-mute">{k.hint}…</span>
              </span>
              <span className="hidden flex-none text-right sm:block">
                <span className="block text-[12px] font-semibold tabular-nums">{k.calls}</span>
                <span className="block text-[10px] text-ghost">calls</span>
              </span>
              {k.scopes.length < 3 ? <Tag>{k.scopes.length} scopes</Tag> : null}
              <button
                type="button"
                onClick={async () => {
                  if (await confirm('Revoke ' + k.name + '?',
                    'Anything using it stops working straight away.',
                    { confirmLabel: 'Revoke', danger: true })) {
                    await api.del('/api/keys/' + k.id);
                    void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
                    toast('Revoked', 'good');
                  }
                }}
                className="flex-none text-[12px] font-semibold text-mute transition-colors hover:text-bad"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[180px] flex-1">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What is it for? e.g. Discord bot"
            maxLength={40}
            onKeyDown={(e) => { if (e.key === 'Enter' && !full) void create(); }}
          />
        </div>
        <Button kind="primary" disabled={busy || full} onClick={() => void create()}>
          {busy ? 'Making…' : full ? 'Limit reached' : 'New key'}
        </Button>
      </div>

      {dialog}
    </Card>
  );
}
